import { defineComponent as _defineComponent } from 'vue'
import { ref as _ref, computed as _computed } from 'vue';

import __MACROS_useVModel from "/vue-macros/define-models/use-vmodel";
import { Vue } from 'vue-property-decorator';
import { onMounted } from 'vue';
import i18n from '@/i18n';
import {
  CustomMetadataService,
  type EntityCustomMetadataDto,
  type EntityCustomMetadataEntryLabelDto,
  type EntityCustomMetadataEntryUserDto,
  type GetCustomMetadataListResponseDto,
  type EntityCustomMetadataEntryTeamDto,
  type EntityCustomMetadataEntryStringDto,
} from '@/api';
import { AssetsTypesEnum } from '@/components/custom-metadata/CustomMetadata';
import SLabel from '@/components/SLabel.vue';
import CustomMetadataLabelSelector from '@/components/selectors/Custom-Metadata-Selector/CustomMetadataLabelSelector.vue';
import CustomMetadataStringSelector from '@/components/selectors/Custom-Metadata-Selector/CustomMetadataStringSelector.vue';
import CustomMetadataUserSelector from '@/components/selectors/Custom-Metadata-Selector/CustomMetadataUserSelector.vue';
import CustomMetadataTeamSelector from '@/components/selectors/Custom-Metadata-Selector/CustomMetadataTeamSelector.vue';
/**
 *
 * Custom Metadata Selector
 * Used to attach custom metadata entries to any entity
 *
 */

interface CustomMetadataSelectorProps {
  assetType: AssetsTypesEnum;
  entityCustomMetadata: EntityCustomMetadataDto[];
  labelsAsColumns?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CustomMetadataSelector',
  props: {
  assetType: null,
  entityCustomMetadata: null,
  labelsAsColumns: { type: Boolean, default: false },
  value: null
},
  emits: ["input"],
  setup(__props) {

const props = __props;



const { value: customMetadataModel } = __MACROS_useVModel(["value", undefined, "input"]);

let fetchedMetadata = _ref<GetCustomMetadataListResponseDto[]>([]);

// Filter metadata based on the asset type
const filteredMetadata = _computed(() => fetchedMetadata.value.filter((metadata) => metadata.authorizedAssets.includes(props.assetType)));

// Constants for metadata types
const METADATA_TYPES = {
  LABEL: 'LABEL',
  STRING: 'STRING',
  USER: 'USER',
  TEAM: 'TEAM',
};

/**
 * Get the existing entity custom metadata from the metadata ID
 * @param metadataId
 */
const getEntityCustomMetadataFromMetadataId = (metadataId: string) => props.entityCustomMetadata.find((metadata) => metadata.id === metadataId);

/**
 * Handles the change event from the sub-selectors:
 * Create a deep copy of entityCustomMetadata and update model
 */
const onSubSelectorUpdate = (
  metadataId: string,
  entries: EntityCustomMetadataEntryLabelDto[] | EntityCustomMetadataEntryUserDto[] | EntityCustomMetadataEntryTeamDto[],
) => {
  const updatedCustomMetadata: EntityCustomMetadataDto[] = filteredMetadata.value.map((metadata) => {
    const existing = props.entityCustomMetadata.find((m) => m.id === metadata.id);
    return {
      id: metadata.id,
      entries: metadata.id === metadataId
        ? (entries as (EntityCustomMetadataEntryLabelDto | EntityCustomMetadataEntryUserDto | EntityCustomMetadataEntryTeamDto)[])
        : (existing?.entries ?? []),
    } as EntityCustomMetadataDto;
  });
  customMetadataModel.value = updatedCustomMetadata;
};

// Type-safe wrapper functions for each selector type
const onStringSelectorUpdate = (metadataId: string, entries: EntityCustomMetadataEntryStringDto[]) => {
  onSubSelectorUpdate(metadataId, entries);
};
const onLabelSelectorUpdate = (metadataId: string, entries: EntityCustomMetadataEntryLabelDto[]) => {
  onSubSelectorUpdate(metadataId, entries);
};

const onUserSelectorUpdate = (metadataId: string, entries: EntityCustomMetadataEntryUserDto[]) => {
  onSubSelectorUpdate(metadataId, entries);
};

const onTeamSelectorUpdate = (metadataId: string, entries: EntityCustomMetadataEntryTeamDto[]) => {
  onSubSelectorUpdate(metadataId, entries);
};

onMounted(() => {
  // Fetch custom metadata when component is mounted
  CustomMetadataService.getAllCustomMetadata()
    .then((response) => {
      fetchedMetadata.value = response.data || [];
    })
    .catch(() => {
      Vue.notify({
        title: i18n.t('custom_metadata.fetch.error_title'),
        text: i18n.t('custom_metadata.fetch.error_text'),
        type: 'error',
      });
    });
});

return { __sfc: true,props, customMetadataModel, fetchedMetadata, filteredMetadata, METADATA_TYPES, getEntityCustomMetadataFromMetadataId, onSubSelectorUpdate, onStringSelectorUpdate, onLabelSelectorUpdate, onUserSelectorUpdate, onTeamSelectorUpdate, _ref, _computed, __MACROS_useVModel, Vue, onMounted, i18n, CustomMetadataService, AssetsTypesEnum, SLabel, CustomMetadataLabelSelector, CustomMetadataStringSelector, CustomMetadataUserSelector, CustomMetadataTeamSelector }
}

})